<script setup lang="ts">
  import { computed } from 'vue';
  import type { LocationQuery } from 'vue-router';

  import { APPS } from '../../constants';
  import { getPersistentItem, removePersistentItem, setPersistentItem } from '../../utils/storage';

  defineOptions({
    name: 'll-context-switcher',
  });

  type AppValues = (typeof APPS)[keyof typeof APPS];

  export interface ContextSwitcherProps {
    /**
     * The current app that is active. Choices are 'marketplace' | 'payments'.
     */
    activeApp: AppValues;

    /**
     * Portal access to both marketplace and payments.
     */
    portalAccess?: {
      commercePortal: boolean;
      paymentsPortal: boolean;
    };

    /**
     * Url to replace the other app if current user does not have portal access.
     */
    limitedAccessUrl: string;

    routeQuery?: LocationQuery;
  }

  const props = withDefaults(defineProps<ContextSwitcherProps>(), {
    portalAccess: () => ({
      commercePortal: false,
      paymentsPortal: false,
    }),
    routeQuery: () => ({}),
  });

  const emit = defineEmits<{
    /**
     * Fires on click of the context switcher.
     * @deprecated Use `switch` instead.
     */
    (e: 'click', urlInfo: { from: string; to: string }): void;
    /**
     * Fires on click of the context switcher.
     */
    (e: 'switch', evt: MouseEvent, urlInfo: { from: string; to: string }): void;
  }>();

  interface ContextItem {
    app: AppValues;
    name: string;
    hasBaseRoute: boolean;
  }

  const CONTEXT_SWITCHER_STORAGE_KEY = '-last-session';

  const routes = computed<ContextItem[]>(() => {
    return [
      {
        app: APPS.MARKETPLACE,
        name: props.portalAccess.commercePortal ? 'dashboard' : props.limitedAccessUrl,
        hasBaseRoute: props.portalAccess.commercePortal,
      },
      {
        app: APPS.PAYMENTS,
        name: props.portalAccess.paymentsPortal ? 'payments' : props.limitedAccessUrl,
        hasBaseRoute: false,
      },
    ];
  });

  /**
   * Saves the current url in local storage before leaving the current app, if user has access to both apps
   * If a previous session url exists, redirect users there instead then remove the local storage item afterward.
   *
   * @param event - Event from user click.
   * @param app - App name of the clicked link, is a key of `APPS`.
   */
  function handleClick(event: MouseEvent, app: AppValues) {
    if (app === props.activeApp) {
      return;
    }

    const currentLocation = window.location.href;
    let destination = (event.target as HTMLAnchorElement).href;

    if (props.portalAccess.paymentsPortal && props.portalAccess.commercePortal) {
      const appRoute = app === APPS.PAYMENTS ? APPS.MARKETPLACE : APPS.PAYMENTS;
      const currentSessionName = `${appRoute}${CONTEXT_SWITCHER_STORAGE_KEY}`;
      const prevSessionName = `${app}${CONTEXT_SWITCHER_STORAGE_KEY}`;

      // save the current url in local storage
      setPersistentItem(currentSessionName, currentLocation, { global: true });

      // get the other app's url from local storage
      const prevSessionUrl = getPersistentItem(prevSessionName, { global: true });

      // if it exists, redirect users to such location and remove the item from local storage
      if (prevSessionUrl) {
        event.preventDefault();
        destination = prevSessionUrl;
        window.location.href = prevSessionUrl;
        removePersistentItem(prevSessionName, { global: true });
      }
    }

    emit('click', { from: currentLocation, to: destination });
    emit('switch', event, { from: currentLocation, to: destination });
  }
</script>

<template>
  <div
    class="stash-context-switcher mx-auto flex w-64 rounded-full border border-white/10 bg-purple-700 p-1.5 text-center"
    data-test="stash-context-switcher"
  >
    <template v-for="route in routes" :key="route.app">
      <!-- render anchor tag to override base route in Payments -->
      <a
        v-if="route.hasBaseRoute"
        class="w-1/2 cursor-pointer rounded-full py-2.5 font-semibold !capitalize leading-none !no-underline"
        :class="props.activeApp === route.app ? 'text-white bg-royal-500' : 'text-ice-500'"
        :href="`/${route.name}`"
        @click="(evt) => handleClick(evt, route.app)"
      >
        {{ route.app }}
      </a>

      <router-link
        v-else
        class="w-1/2 cursor-pointer rounded-full py-2.5 font-semibold !capitalize leading-none !no-underline"
        :class="props.activeApp === route.app ? 'text-white bg-royal-500' : 'text-ice-500'"
        :to="{ name: route.name, query: routeQuery }"
        :data-test-route="JSON.stringify({ name: route.name, query: routeQuery })"
        @click="(evt) => handleClick(evt, route.app)"
      >
        {{ route.app }}
      </router-link>
    </template>
  </div>
</template>
